import './page-header.scss';
import React, {useMemo, useRef} from 'react';
import {Box, Grid, GridSize, useMediaQuery, Avatar, Typography} from '@mui/material';
import {avatarSizesAndCoefficients} from '../../utils/constants/avatar-sizes-and-coefficients';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';

/**
 * Header used in our screens.
 */
export const PageHeader = (props: {
  title?: string;
  isSmallTitle?: boolean;
  titleAvatar?: {
    type: 'user' | 'entity' | 'other';
    url?: string;
  };
  leftButtons?: React.ReactNode;
  rightButtons?: React.ReactNode;
  filters?: React.ReactNode;
  showSearch?: boolean;
  searchLabel?: string;
  searchText?: string;
  searchOnChange?: (text: string) => void;
  searchOnSubmit?: () => void;
  disableSearch?: boolean;
}) => {
  // Media query
  const mediaQueryMatches: boolean = useMediaQuery('(max-width:600px)');

  // Refs
  const searchBoxRef = useRef();

  // Calculate grid values
  let titleWidth: GridSize = 12;
  let leftButtonsWidth: GridSize = 12;
  let rightButtonsWidth: GridSize = 12;
  let showLeftButtons: boolean = false;
  let showTitle: boolean = false;
  let showRightButtons: boolean = false;
  if (props.title && (props.leftButtons || props.rightButtons)) {
    showTitle = true;
    titleWidth = mediaQueryMatches ? 12 : 6;
    if (mediaQueryMatches) {
      if (props.leftButtons && props.rightButtons) {
        leftButtonsWidth = 6;
        rightButtonsWidth = 6;
        showLeftButtons = true;
        showRightButtons = true;
      } else {
        if (props.leftButtons) {
          leftButtonsWidth = 12;
          showLeftButtons = true;
          showRightButtons = false;
        } else {
          rightButtonsWidth = 12;
          showLeftButtons = false;
          showRightButtons = true;
        }
      }
    } else {
      leftButtonsWidth = 3;
      rightButtonsWidth = 3;
      showLeftButtons = true;
      showRightButtons = true;
    }
  } else if (!props.title && (props.leftButtons || props.rightButtons)) {
    showTitle = false;

    if (props.leftButtons && props.rightButtons) {
      leftButtonsWidth = 6;
      rightButtonsWidth = 6;
      showLeftButtons = true;
      showRightButtons = true;
    } else {
      if (props.leftButtons) {
        leftButtonsWidth = 12;
        showLeftButtons = true;
        showRightButtons = false;
      } else {
        rightButtonsWidth = 12;
        showLeftButtons = false;
        showRightButtons = true;
      }
    }
  } else if (props.title && !props.leftButtons && !props.rightButtons) {
    showTitle = true;
    showLeftButtons = false;
    showRightButtons = false;
    titleWidth = 12;
  }

  // Avatar sizes, coefficients and default icon
  const avatarSize: number =
    props.isSmallTitle === true
      ? avatarSizesAndCoefficients.avatarSizes.large
      : avatarSizesAndCoefficients.avatarSizes.giant;
  const iconSize: number = avatarSize * avatarSizesAndCoefficients.iconSizeCoefficient;
  let defaultAvatarIcon: React.ReactElement = <></>;
  let avatarBorderRadius: string | undefined;
  if (props.titleAvatar) {
    switch (props.titleAvatar.type) {
      case 'user':
        defaultAvatarIcon = <PersonIcon sx={{fontSize: iconSize}} />;
        avatarBorderRadius = `${avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.circle}px`;
        break;
      case 'entity':
        defaultAvatarIcon = <BusinessIcon sx={{fontSize: iconSize}} />;
        avatarBorderRadius = `${
          avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersLow
        }px`;
        break;
      default:
        defaultAvatarIcon = <></>;
        avatarBorderRadius = `${
          avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersHigh
        }px`;
        break;
    }
  }

  const renderingAnyComponent = useMemo(() => {
    return showTitle || showLeftButtons || showRightButtons || props.showSearch || !!props.filters;
  }, [showTitle, showLeftButtons, showRightButtons, props.showSearch, props.filters]);

  // Default result
  return (
    <Grid
      container
      className="page-header-grid"
      sx={{
        minHeight: showTitle ? '56px' : undefined,
        marginTop: renderingAnyComponent ? '16px' : undefined,
        marginBottom: renderingAnyComponent ? '24px' : undefined,
      }}
    >
      {mediaQueryMatches ? (
        <>
          {showTitle ? (
            <Grid
              item
              xs={titleWidth}
              className="page-header-title-container"
              sx={{
                marginBottom: showLeftButtons || showRightButtons ? '32px' : '0px',
              }}
            >
              {props.titleAvatar && (
                <Box className="page-header-title-avatar">
                  <Avatar
                    sx={{
                      width: avatarSize,
                      height: avatarSize,
                      borderRadius: avatarBorderRadius,
                    }}
                    alt={props.title}
                    src={props.titleAvatar?.url}
                  >
                    {defaultAvatarIcon}
                  </Avatar>
                </Box>
              )}
              <Typography
                variant={props.isSmallTitle === true ? 'h5' : 'h4'}
                className="page-header-title"
              >
                {props.title}
              </Typography>
            </Grid>
          ) : undefined}
          {showLeftButtons ? (
            <Grid item xs={leftButtonsWidth} className="page-header-left-grid">
              <Box>{props.leftButtons}</Box>
            </Grid>
          ) : undefined}
          {showRightButtons ? (
            <Grid item xs={rightButtonsWidth} className="page-header-right-grid">
              <Box>{props.rightButtons}</Box>
            </Grid>
          ) : undefined}
        </>
      ) : (
        <>
          {showLeftButtons ? (
            <Grid item xs={leftButtonsWidth} className="page-header-left-grid">
              <Box>{props.leftButtons}</Box>
            </Grid>
          ) : undefined}
          {showTitle ? (
            <Grid item xs={titleWidth} className="page-header-title-container">
              {props.titleAvatar && (
                <Box className="page-header-title-avatar">
                  <Avatar
                    sx={{
                      width: avatarSize,
                      height: avatarSize,
                      borderRadius: avatarBorderRadius,
                    }}
                    alt={props.title}
                    src={props.titleAvatar?.url}
                  >
                    {defaultAvatarIcon}
                  </Avatar>
                </Box>
              )}
              <Typography
                variant={props.isSmallTitle === true ? 'h5' : 'h4'}
                className="page-header-title"
              >
                {props.title}
              </Typography>
            </Grid>
          ) : undefined}
          {showRightButtons ? (
            <Grid item xs={rightButtonsWidth} className="page-header-right-grid">
              <Box>{props.rightButtons}</Box>
            </Grid>
          ) : undefined}
        </>
      )}
      {props.showSearch && (
        <Grid
          item
          xs={12}
          className="page-header-search-container"
          sx={{marginTop: showTitle || showLeftButtons || showRightButtons ? '32px' : undefined}}
        >
          <TextField
            inputRef={searchBoxRef}
            variant="outlined"
            color="neutral"
            disabled={props.disableSearch}
            sx={{maxWidth: '360px', width: '100%'}}
            label={props.searchLabel}
            value={props.searchText}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              // If change event was defined
              if (props.searchOnChange) {
                // Invoke it
                props.searchOnChange(event.target.value);
              }
            }}
            onBlur={() => {
              // If event was defined
              if (props.searchOnSubmit) {
                // Trigger action
                props.searchOnSubmit();

                // Blur input
                (searchBoxRef.current as any).blur();
              }
            }}
            onKeyUp={(event: React.KeyboardEvent) => {
              // Number 13 is the "Enter" key on the keyboard
              if (event.key === 'Enter') {
                // Cancel the default action, if needed
                event.preventDefault();

                // Blur input
                (searchBoxRef.current as any).blur();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      // Blur input
                      (searchBoxRef.current as any).blur();
                    }}
                    onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) => {
                      event.preventDefault();
                    }}
                    edge="end"
                  >
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
      )}
      {props.filters && (
        <Grid
          item
          xs={12}
          sx={{
            marginTop: props.showSearch
              ? '24px'
              : showTitle || showLeftButtons || showRightButtons
                ? '32px'
                : undefined,
          }}
        >
          {props.filters}
        </Grid>
      )}
    </Grid>
  );
};
